import { useEffect, useState } from "react";

import { faker } from "@faker-js/faker";
import type { Meta, StoryObj } from "@storybook/react";

import { fakeGooglePlacesAddress } from "@/__mocks__/mockBaseTypes";
import { fakeMultiSelectOptions } from "@/__mocks__/multiselect";
import FormField from "@/ui/composed/form/FormField";
import { FormFieldType } from "@/ui/composed/form/types";
import type { FormFieldProps } from "@/ui/composed/form/types";
import { Box } from "@/ui/layout";

const StoryMeta: Meta<FormFieldProps> = {
  title: "Composed/Form/Form Field",
  component: FormField,
  argTypes: {
    hasError: { control: "boolean" },
    isDisabled: { control: "boolean" },
    isReadOnly: { control: "boolean" },
    isRequired: { control: "boolean" },
    leftText: { control: "text" },
    onChange: { action: "Changed" },
    type: { control: "select", options: Object.values(FormFieldType) },
  },
  args: {
    hasError: false,
    isDisabled: false,
    isReadOnly: false,
    isRequired: true,
    placeholder: "Enter value",
    rightText: "",
    subLabel: "",
  },

  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const [value, setValue] = useState<FormFieldProps["value"]>(args.value);

    useEffect(() => {
      setValue(args.value);
    }, [args.value]);

    return (
      <Box width="100%">
        <FormField
          {...({
            ...args,
            onChange: (newValue: FormFieldProps["value"]) => {
              setValue(newValue);
            },
            value,
          } as FormFieldProps)}
        />
      </Box>
    );
  },
};

type Story = StoryObj<FormFieldProps>;

const AddressField: Story = {
  args: {
    leftText: "Main address",
    type: FormFieldType.Address,
    value: {
      ...fakeGooglePlacesAddress({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        state: faker.location.state({ abbreviated: true }),
      }),
    },
  },
};

const CheckBoxField: Story = {
  args: {
    leftText: "Push notifications",
    rightText: "Recommended",
    subLabel:
      "We recommend turning notifications on to stay on track with reminders, assessments, and important updates about your treatment.",
    type: FormFieldType.CheckBox,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    value: true,
  },
};

const DateField: Story = {
  args: {
    leftText: "Date of birth",
    type: FormFieldType.Date,
    value: new Date("1991-03-12"),
  },
};

const SelectField: Story = {
  args: {
    leftText: "Preferred healthcare language",
    options: fakeMultiSelectOptions(10),
    subLabel:
      "We’ll do our best to match you with providers who speak your preferred language when available.",
    type: FormFieldType.Select,
  },
};

const TextField: Story = {
  args: {
    leftText: "Email address",
    type: FormFieldType.Text,
    value: faker.internet.email(),
  },
};

export { AddressField, CheckBoxField, DateField, SelectField, TextField };
export default StoryMeta;
